// Admin — MyPay (mypay.ly) gateway settings. Mirrors the NUMO admin page shape:
// enable/disable toggle, environment picker, credentials with masked preview,
// wallet-topup + order-payment toggles, min/max amounts, and a "ping" button
// that hits /admin/payments/mypay/test to validate credentials without side
// effects. The webhook URL is shown so the merchant can paste it into MyPay
// merchant dashboard.
import { useEffect, useState } from "react";
import api from "@/api/client";
import TopBar from "@/components/TopBar";
import BackButton from "@/components/BackButton";
import {
  CreditCard, Save, Radio, Eye, EyeOff, Loader2, CheckCircle2, XCircle, AlertTriangle,
  Copy, RefreshCw, PlugZap,
} from "lucide-react";

const EMPTY_FORM = {
  enabled: false,
  env: "sandbox",
  client_id: "",
  secret_id: "",
  webhook_secret: "",
  wallet_topup_enabled: true,
  order_payment_enabled: false,
  min_amount: 1,
  max_amount: 5000,
  fee_percent: 2.5,
};

export default function AdminMyPay() {
  const [loading, setLoading] = useState(true);
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [show, setShow] = useState({ client_id: false, secret_id: false, webhook_secret: false });
  const [busy, setBusy] = useState(false);
  const [pinging, setPinging] = useState(false);
  const [pingResult, setPingResult] = useState(null);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [topups, setTopups] = useState({ topups: [], total: 0, completed: 0 });
  const [webhookLogs, setWebhookLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(false);

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get("/admin/payments/mypay/config");
      setCfg(data);
      setForm({
        enabled: !!data.enabled,
        env: data.env || "sandbox",
        client_id: "",
        secret_id: "",
        webhook_secret: "",
        wallet_topup_enabled: !!data.wallet_topup_enabled,
        order_payment_enabled: !!data.order_payment_enabled,
        min_amount: data.min_amount ?? 1,
        max_amount: data.max_amount ?? 5000,
        fee_percent: data.fee_percent ?? 2.5,
      });
    } catch (e) {
      setErr(e?.response?.data?.detail || "فشل تحميل الإعدادات");
    } finally { setLoading(false); }
    try {
      const { data } = await api.get("/admin/payments/mypay/topups", { params: { limit: 30 } });
      setTopups(data);
    } catch { /* ignore */ }
    try {
      const { data } = await api.get("/admin/payments/mypay/webhook-logs", { params: { limit: 30 } });
      setWebhookLogs(data.logs || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true); setMsg(""); setErr("");
    // Only send secret fields that were actually changed, so the empty-input
    // scenario keeps the stored value intact.
    const payload = {
      enabled: form.enabled,
      env: form.env,
      wallet_topup_enabled: form.wallet_topup_enabled,
      order_payment_enabled: form.order_payment_enabled,
      min_amount: parseFloat(form.min_amount),
      max_amount: parseFloat(form.max_amount),
      fee_percent: parseFloat(form.fee_percent),
    };
    if (form.client_id) payload.client_id = form.client_id;
    if (form.secret_id) payload.secret_id = form.secret_id;
    if (form.webhook_secret) payload.webhook_secret = form.webhook_secret;
    try {
      const { data } = await api.patch("/admin/payments/mypay/config", payload);
      setCfg(data);
      setForm((f) => ({ ...f, client_id: "", secret_id: "", webhook_secret: "" }));
      setMsg("تم حفظ الإعدادات بنجاح");
    } catch (e) {
      setErr(e?.response?.data?.detail || "فشل الحفظ");
    } finally { setBusy(false); }
  };

  const ping = async () => {
    setPinging(true); setPingResult(null);
    try {
      const { data } = await api.post("/admin/payments/mypay/test");
      setPingResult(data);
    } catch (e) {
      setPingResult({ ok: false, error: e?.response?.data?.detail || "فشل الاتصال" });
    } finally { setPinging(false); }
  };

  const copy = (text) => {
    try { navigator.clipboard?.writeText(text); setMsg("تم النسخ"); setTimeout(() => setMsg(""), 1200); } catch { /* noop */ }
  };

  if (loading || !cfg) return (
    <div className="min-h-screen bg-[var(--surface-alt)]">
      <TopBar/>
      <div className="max-w-4xl mx-auto p-6 flex items-center gap-2 text-sm text-[var(--text-secondary)]">
        <Loader2 size={16} className="animate-spin"/> جاري التحميل...
      </div>
    </div>
  );

  const readyToActivate = (cfg.client_id_set || form.client_id) && (cfg.secret_id_set || form.secret_id) && (cfg.webhook_secret_set || form.webhook_secret);

  return (
    <div className="min-h-screen bg-[var(--surface-alt)]" data-testid="admin-mypay-page">
      <TopBar/>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-4 flex items-center gap-2">
        <BackButton to="/admin" variant="pill"/>
        <div className="flex-1">
          <h1 className="text-2xl font-black" style={{fontFamily:"Cairo"}}>MyPay — بوابة الدفع</h1>
          <p className="text-xs text-[var(--text-secondary)]">إعداد mypay.ly (يسر / مصرفي / معاملات / إدفعلي / صحارى)</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 pb-20 space-y-4 mt-4">
        {/* Status banner */}
        <div
          className="rounded-3xl overflow-hidden text-white p-5 talabya-shadow-float"
          style={{
            background: cfg.enabled
              ? "linear-gradient(135deg, #16A34A 0%, #065F46 100%)"
              : "linear-gradient(135deg, #B45309 0%, #78350F 100%)",
          }}
        >
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-2xl bg-white/25 flex items-center justify-center shrink-0">
              {cfg.enabled ? <CheckCircle2 size={22}/> : <AlertTriangle size={22}/>}
            </div>
            <div className="flex-1">
              <div className="font-black text-lg" style={{fontFamily:"Cairo"}}>
                {cfg.enabled ? "MyPay مفعّلة" : "MyPay لم تُفعّل بعد"}
              </div>
              <div className="text-sm text-white/90 mt-1 leading-relaxed">
                {cfg.enabled
                  ? `الوضع: ${cfg.env === "production" ? "إنتاج مباشر" : "تجريبي (Sandbox)"} — Client: ${cfg.client_id_masked || "—"}`
                  : "أدخل بيانات الاعتماد من لوحة تحكم MyPay، ثم فعّل البوابة."}
              </div>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
            <Metric label="إجمالي المحاولات" value={topups.total}/>
            <Metric label="مكتملة" value={topups.completed}/>
            <Metric label="نسبة النجاح" value={topups.total ? `${Math.round((topups.completed/topups.total)*100)}%` : "—"}/>
          </div>
        </div>

        {/* Webhook URL — copy button for pasting into MyPay merchant dashboard */}
        <div className="bg-white rounded-3xl talabya-shadow-soft p-5" data-testid="mypay-webhook-url">
          <div className="flex items-center gap-2 mb-2">
            <PlugZap size={18} className="text-primary-brand"/>
            <h2 className="font-black text-lg" style={{fontFamily:"Cairo"}}>رابط الويبهوك</h2>
          </div>
          <p className="text-[11px] text-[var(--text-secondary)] mb-2">
            انسخ هذا الرابط وأضفه في لوحة تحكم MyPay ضمن إعدادات Webhook. سيرسل MyPay إشعارات الدفع إلى هذا العنوان.
          </p>
          <div className="flex items-stretch gap-2">
            <input readOnly value={cfg.webhook_url} dir="ltr"
              className="flex-1 px-3 py-2.5 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] font-mono text-xs" />
            <button onClick={() => copy(cfg.webhook_url)} className="px-4 rounded-xl bg-primary-brand text-white font-black text-xs flex items-center gap-1" data-testid="copy-webhook-url">
              <Copy size={14}/> نسخ
            </button>
          </div>
        </div>

        {/* Credentials */}
        <div className="bg-white rounded-3xl talabya-shadow-soft p-5" data-testid="mypay-credentials">
          <div className="flex items-center gap-2 mb-3">
            <CreditCard size={18} className="text-primary-brand"/>
            <h2 className="font-black text-lg" style={{fontFamily:"Cairo"}}>بيانات الاعتماد</h2>
          </div>
          <SecretField
            label={`Client ID ${cfg.client_id_set ? "— " + cfg.client_id_masked : ""}`}
            hint="يوفّره MyPay في لوحة تحكم التاجر"
            value={form.client_id} onChange={(v) => setForm({...form, client_id: v})}
            show={show.client_id} onToggle={() => setShow({...show, client_id: !show.client_id})}
            placeholder={cfg.client_id_set ? "اترك فارغاً للإبقاء على القيمة الحالية" : "الصق Client ID"}
            testid="input-client-id"
          />
          <SecretField
            label={`Secret ID ${cfg.secret_id_set ? "— " + cfg.secret_id_masked : ""}`}
            hint="مفتاح سري — لا يظهر مرة أخرى بعد الحفظ"
            value={form.secret_id} onChange={(v) => setForm({...form, secret_id: v})}
            show={show.secret_id} onToggle={() => setShow({...show, secret_id: !show.secret_id})}
            placeholder={cfg.secret_id_set ? "اترك فارغاً للإبقاء على القيمة الحالية" : "الصق Secret ID"}
            testid="input-secret-id"
          />
          <SecretField
            label={`Webhook Secret ${cfg.webhook_secret_set ? "— " + cfg.webhook_secret_masked : ""}`}
            hint="يُستخدم للتحقق من توقيع HMAC-SHA256 للطلبات القادمة من MyPay"
            value={form.webhook_secret} onChange={(v) => setForm({...form, webhook_secret: v})}
            show={show.webhook_secret} onToggle={() => setShow({...show, webhook_secret: !show.webhook_secret})}
            placeholder={cfg.webhook_secret_set ? "اترك فارغاً للإبقاء على القيمة الحالية" : "الصق Webhook Secret"}
            testid="input-webhook-secret"
          />

          {/* Ping button */}
          <div className="mt-3 flex items-center gap-2">
            <button
              onClick={ping}
              disabled={pinging || !readyToActivate}
              className="px-4 py-2 rounded-xl bg-white border border-[var(--border)] text-sm font-bold hover:bg-primary-light disabled:opacity-50 flex items-center gap-2"
              data-testid="ping-btn"
            >
              {pinging ? <Loader2 size={14} className="animate-spin"/> : <RefreshCw size={14}/>}
              اختبار الاتصال
            </button>
            {pingResult && (
              <div className={`text-sm font-bold flex items-center gap-1 ${pingResult.ok ? "text-emerald-700" : "text-red-700"}`} data-testid="ping-result">
                {pingResult.ok ? <><CheckCircle2 size={16}/> اتصال ناجح ({pingResult.token_preview})</> : <><XCircle size={16}/> فشل: {pingResult.error}</>}
              </div>
            )}
          </div>
        </div>

        {/* Mode + toggles */}
        <div className="bg-white rounded-3xl talabya-shadow-soft p-5">
          <h2 className="font-black text-lg mb-3" style={{fontFamily:"Cairo"}}>الإعدادات العامة</h2>
          <ToggleRow
            testid="toggle-enabled"
            label="تفعيل بوابة MyPay"
            desc="عند التفعيل يظهر خيار الدفع للزبائن"
            value={form.enabled}
            onChange={(v) => setForm({...form, enabled: v})}
            disabled={!readyToActivate}
            disabledHint="أدخل Client ID و Secret ID و Webhook Secret أولاً"
          />
          <div className="mt-3 flex items-center gap-2 p-3 bg-[var(--surface-alt)] rounded-2xl">
            <Radio size={16} className="text-primary-brand"/>
            <span className="text-sm font-bold">وضع التشغيل</span>
            <div className="mr-auto flex gap-1">
              {[
                { k: "sandbox", label: "تجريبي" },
                { k: "production", label: "إنتاج مباشر" },
              ].map((m) => (
                <button key={m.k}
                  onClick={() => setForm({...form, env: m.k})}
                  className={`px-3 py-1.5 rounded-lg text-xs font-black ${form.env === m.k ? "bg-primary-brand text-white" : "bg-white text-[var(--text-secondary)] border border-[var(--border)]"}`}
                  data-testid={`mode-${m.k}`}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>
          <ToggleRow
            testid="toggle-wallet-topup"
            label="السماح بشحن المحفظة عبر MyPay"
            desc="يظهر زر شحن المحفظة في الملف الشخصي وشاشة الدفع"
            value={form.wallet_topup_enabled}
            onChange={(v) => setForm({...form, wallet_topup_enabled: v})}
          />
          <ToggleRow
            testid="toggle-order-payment"
            label="السماح بدفع الطلبات مباشرة عبر MyPay"
            desc="خيار الدفع الفوري بجانب النقد والمحفظة (يتطلب دعم اضافي)"
            value={form.order_payment_enabled}
            onChange={(v) => setForm({...form, order_payment_enabled: v})}
          />
          <div className="mt-3 grid grid-cols-2 gap-2">
            <Field label="الحد الأدنى للشحن (د.ل)">
              <input type="number" step="0.5" value={form.min_amount}
                onChange={(e) => setForm({...form, min_amount: parseFloat(e.target.value || "0")})}
                dir="ltr"
                className="w-full px-3 py-2 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] focus:bg-white text-center font-bold"
                data-testid="input-min-amount"/>
            </Field>
            <Field label="الحد الأقصى للشحن (د.ل)">
              <input type="number" value={form.max_amount}
                onChange={(e) => setForm({...form, max_amount: parseFloat(e.target.value || "0")})}
                dir="ltr"
                className="w-full px-3 py-2 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] focus:bg-white text-center font-bold"
                data-testid="input-max-amount"/>
            </Field>
          </div>
          <div className="mt-3">
            <Field label="نسبة رسوم الدفع الإلكتروني (%) — تُضاف فوق مبلغ الشحن، والزبون يدفعها">
              <div className="flex items-center gap-2">
                <input type="number" step="0.1" min="0" max="20" value={form.fee_percent}
                  onChange={(e) => setForm({...form, fee_percent: parseFloat(e.target.value || "0")})}
                  dir="ltr"
                  className="w-24 px-3 py-2 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] focus:bg-white text-center font-bold"
                  data-testid="input-fee-percent"/>
                <span className="text-sm font-black text-primary-brand">%</span>
                <span className="text-[11px] text-[var(--text-secondary)] mr-2">
                  مثال: زبون يشحن 100 د.ل ← يدفع {(100 * (1 + (parseFloat(form.fee_percent) || 0) / 100)).toFixed(2)} د.ل
                </span>
              </div>
            </Field>
          </div>
        </div>

        {msg && <div className="bg-green-50 text-green-700 px-4 py-3 rounded-xl text-sm font-bold" data-testid="save-msg">{msg}</div>}
        {err && <div className="bg-red-50 text-red-600 px-4 py-3 rounded-xl text-sm font-bold" data-testid="save-err">{err}</div>}

        <button
          onClick={save}
          disabled={busy}
          className="w-full py-4 rounded-2xl bg-primary-brand text-white font-black talabya-shadow-float disabled:opacity-60 flex items-center justify-center gap-2"
          data-testid="save-btn"
        >
          {busy ? <Loader2 size={16} className="animate-spin"/> : <Save size={16}/>}
          {busy ? "جاري الحفظ..." : "حفظ الإعدادات"}
        </button>

        {/* Recent top-ups */}
        {topups.topups.length > 0 && (
          <div className="bg-white rounded-3xl talabya-shadow-soft overflow-hidden" data-testid="mypay-topups-list">
            <div className="px-5 py-3 border-b border-[var(--border)] flex items-center justify-between">
              <h3 className="font-black" style={{fontFamily:"Cairo"}}>آخر عمليات الشحن</h3>
              <span className="text-xs text-[var(--text-secondary)]">{topups.topups.length}</span>
            </div>
            <ul className="divide-y divide-[var(--border)]">
              {topups.topups.slice(0, 20).map((t) => (
                <TopupRow key={t.custom} t={t} onCredit={load} />
              ))}
            </ul>
          </div>
        )}

        {/* Webhook logs — diagnostic view for admins */}
        <div className="bg-white rounded-3xl talabya-shadow-soft overflow-hidden" data-testid="mypay-webhook-logs">
          <button
            onClick={() => setShowLogs((v) => !v)}
            className="w-full px-5 py-3 border-b border-[var(--border)] flex items-center justify-between hover:bg-[var(--surface-alt)] transition"
            data-testid="toggle-webhook-logs"
          >
            <div className="flex items-center gap-2">
              <PlugZap size={16} className="text-primary-brand"/>
              <h3 className="font-black" style={{fontFamily:"Cairo"}}>سجلات الويبهوك</h3>
              <span className="text-xs text-[var(--text-secondary)]">({webhookLogs.length})</span>
            </div>
            <span className="text-xs font-bold text-primary-brand">{showLogs ? "إخفاء" : "عرض"}</span>
          </button>
          {showLogs && (
            <div className="p-3">
              {webhookLogs.length === 0 ? (
                <div className="text-center text-xs text-[var(--text-secondary)] py-6" data-testid="no-webhook-logs">
                  <AlertTriangle className="inline-block ml-1" size={14}/>
                  <b>لا توجد سجلات ويبهوك حتى الآن.</b>
                  <br/>هذا يعني أن MyPay لم يتصل بعنوان الويبهوك أبداً — تحقق من:
                  <br/>1. إعدادات MyPay التاجر (Webhook URL يجب أن يكون `{cfg?.webhook_url || "..."}`)
                  <br/>2. المفتاح Webhook Secret في MyPay يجب أن يطابق المخزّن هنا
                </div>
              ) : (
                <ul className="space-y-2">
                  {webhookLogs.map((log) => (
                    <li key={log.id} className="text-xs bg-[var(--surface-alt)] rounded-xl p-2.5 border border-[var(--border)]">
                      <div className="flex items-center justify-between mb-1">
                        <span className={`px-2 py-0.5 rounded-full font-black text-[10px] ${
                          log.result === "credited" ? "bg-green-100 text-green-700"
                          : log.result === "already_credited" ? "bg-blue-100 text-blue-700"
                          : "bg-red-100 text-red-700"
                        }`}>{log.result || "?"}</span>
                        <span className="text-[10px] text-[var(--text-secondary)]" dir="ltr">
                          {new Date(log.received_at).toLocaleString("ar")}
                        </span>
                      </div>
                      {log.custom && <div className="font-mono text-[10px] truncate">custom: {log.custom}</div>}
                      {log.signature_header && <div className="font-mono text-[10px]" dir="ltr">sig: {log.signature_header}</div>}
                      {log.expected_preview && <div className="font-mono text-[10px] text-red-600" dir="ltr">expected: {log.expected_preview}</div>}
                      {log.body_preview && (
                        <details className="mt-1">
                          <summary className="text-[10px] text-[var(--text-secondary)] cursor-pointer">عرض البيانات</summary>
                          <pre className="mt-1 text-[10px] whitespace-pre-wrap break-all" dir="ltr">{log.body_preview}</pre>
                        </details>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TopupRow({ t, onCredit }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const isPending = !t.credited && t.status === "pending";
  const isFailed = !t.credited && ["failed", "cancelled"].includes(String(t.status || "").toLowerCase());
  const isDone = t.credited || t.status === "completed";

  const doManualCredit = async () => {
    if (!window.confirm(`تأكيد شحن ${t.amount} د.ل يدوياً لهذه العملية؟ لن يتم شحنها مرتين حتى لو وصل webhook متأخراً.`)) return;
    setBusy(true); setErr("");
    try {
      await api.post(`/admin/payments/mypay/topups/${t.custom}/manual-credit`);
      onCredit?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "فشلت العملية");
    } finally { setBusy(false); }
  };

  return (
    <li className="p-3 flex items-center gap-3 text-xs" data-testid={`topup-${t.custom}`}>
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
        isDone ? "bg-green-50 text-green-600" : isPending ? "bg-yellow-50 text-yellow-600" : "bg-red-50 text-red-500"
      }`}>
        {isDone ? <CheckCircle2 size={14}/> : isPending ? <Loader2 size={14}/> : <XCircle size={14}/>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-mono font-bold truncate">{t.custom}</div>
        {(t.user_name || t.user_phone) && (
          <div className="text-[11px] font-black text-primary-brand mt-0.5 flex items-center gap-1.5 flex-wrap" data-testid={`topup-user-${t.custom}`}>
            <span>👤 {t.user_name || "—"}</span>
            {t.user_phone && (
              <a href={`tel:${t.user_phone}`} className="font-mono hover:underline">
                📞 {t.user_phone}
              </a>
            )}
          </div>
        )}
        <div className="text-[10px] text-[var(--text-secondary)]">
          {new Date(t.created_at).toLocaleString("ar")} · {t.gateway_alias || t.gateway || "auto"}
        </div>
        {err && <div className="text-red-600 mt-1">{err}</div>}
      </div>
      <div className="text-left shrink-0">
        <div className="font-black">{Number(t.amount || 0).toFixed(2)} د.ل</div>
        <div className="text-[10px] text-[var(--text-secondary)]">{t.status}</div>
      </div>
      {(isPending || isFailed) && (
        <button
          onClick={doManualCredit}
          disabled={busy}
          className="shrink-0 px-2 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-black disabled:opacity-60"
          data-testid={`manual-credit-${t.custom}`}
          title="شحن المحفظة يدوياً — آمن (لا يشحن مرتين)"
        >
          {busy ? <Loader2 size={12} className="animate-spin"/> : "شحن يدوياً"}
        </button>
      )}
    </li>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-bold text-[var(--text-secondary)] mb-1">{label}</label>
      {children}
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="bg-white/15 rounded-xl p-2 text-center">
      <div className="text-[10px] text-white/80">{label}</div>
      <div className="font-black text-lg">{value}</div>
    </div>
  );
}

function ToggleRow({ label, desc, value, onChange, disabled, disabledHint, testid }) {
  return (
    <div className={`flex items-start gap-3 py-3 border-t border-[var(--border)] first:border-t-0 ${disabled ? "opacity-60" : ""}`} data-testid={testid}>
      <div className="flex-1">
        <div className="font-bold text-sm">{label}</div>
        <div className="text-[11px] text-[var(--text-secondary)]">{disabled ? disabledHint : desc}</div>
      </div>
      <button
        onClick={() => !disabled && onChange(!value)}
        disabled={disabled}
        className={`relative w-12 h-7 rounded-full transition-colors shrink-0 ${value ? "bg-primary-brand" : "bg-gray-300"} disabled:cursor-not-allowed`}
        aria-label={`toggle ${label}`}
      >
        <span className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow transition-all ${value ? "right-1" : "right-6"}`}/>
      </button>
    </div>
  );
}

function SecretField({ label, hint, value, onChange, show, onToggle, placeholder, testid }) {
  return (
    <div className="mb-3">
      <label className="block text-xs font-bold text-[var(--text-secondary)] mb-1">{label}</label>
      <div className="relative">
        <input
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          dir="ltr"
          placeholder={placeholder}
          className="w-full px-4 py-3 pr-10 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] focus:bg-white focus:border-primary-brand focus:outline-none font-mono text-xs"
          data-testid={testid}
        />
        <button type="button" onClick={onToggle} className="absolute top-3 left-3 text-[var(--text-secondary)]" aria-label="toggle visibility">
          {show ? <EyeOff size={16}/> : <Eye size={16}/>}
        </button>
      </div>
      {hint && <p className="text-[10px] text-[var(--text-secondary)] mt-1">{hint}</p>}
    </div>
  );
}
